/* The signed-in user's OWN shop / warehouse / account, as the browser knows it.
 *
 * One user = one group (PC_GRP_ID: 1 admin, 2 shop, 3 warehouse, 4 pay account) tied to one
 * entity (PC_GRP_ATT), as stored at login; its name comes from OTHER_DATA. Several groups per user
 * are parked (2026-11-02, branch wip/groups-and-rights-2026-11-02).
 *
 * For filling and limiting the user's OWN fields only — never a security boundary. The `screen`
 * arguments are kept for the call sites; with one group the screen guard (Role Rights) already
 * decides whether the screen opens at all.
 */

const KIND_GROUP = { shop: 2, warehouse: 3, account: 4 };
const NAME_FIELD = { 2: 'SP_NAME', 3: 'WAREHOUSE_NAME', 4: 'ACCOUNT_TITLE' };

function read(key) {
    try {
        const v = localStorage.getItem(key);
        return v && v !== 'null' && v !== 'undefined' ? v : '';
    } catch (e) {
        return '';
    }
}

function otherData() {
    try {
        return JSON.parse(read('OTHER_DATA') || '{}') || {};
    } catch (e) {
        return {};
    }
}

/* The user's own shops / warehouses / accounts of one kind: [{id, name}] (id text) — at most one. */
export function myEntities(kind) {
    const grp = Number(read('PC_GRP_ID'));
    const id = read('PC_GRP_ATT');
    if (!id || grp !== KIND_GROUP[kind]) return [];
    return [{ id: String(id), name: String(otherData()[NAME_FIELD[grp]] || '') }];
}

/* The id to work with by default on a screen: the user's own entity of that kind, else the login's
 * single id (PC_GRP_ATT). Text. */
export function defaultOwnId(kind) {
    const own = myEntities(kind);
    return own.length ? own[0].id : read('PC_GRP_ATT');
}

/* Is the user in an admin (whole-system) group? Such a user picks from every shop / account. */
export function seesAll() {
    return read('PC_GRP_ID') === '1';
}

/* A pay-account user's partner shops (ShopPartnerDetail on their account, from login): [{id, name}]. */
export function myPartnerShops() {
    if (read('PC_GRP_ID') !== String(KIND_GROUP.account)) return [];
    let list = [];
    try {
        const raw = otherData().ShopPartnerDetail;
        list = raw ? JSON.parse(raw) : [];
    } catch (e) {
        list = [];
    }
    const seen = new Set();
    const out = [];
    for (const entry of Array.isArray(list) ? list : []) {
        const shop = entry && entry.ShopName;
        if (!shop || shop.value === undefined || shop.value === null) continue;
        const id = String(shop.value);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({ id, name: shop.label || '' });
    }
    return out;
}

/* What a screen's OWN shop / account box may offer (user, 2026-11-02: only the user's own; one →
 * selected and locked): null = no limit (admin), else [{id, name}] — the user's own of that kind,
 * plus (withPartners, for shop boxes on the account reports) their account's partner shops. */
export function ownLimit(kind, screen, withPartners = false) {
    if (seesAll()) return null;
    const out = myEntities(kind);
    if (withPartners && kind === 'shop') {
        const seen = new Set(out.map(e => e.id));
        for (const s of myPartnerShops()) if (!seen.has(s.id)) { seen.add(s.id); out.push(s); }
    }
    return out;
}

/* `options` ([{value, label, …}]) cut down to `limit` (from ownLimit); null keeps them all. A limited
 * id the options lack is added with its own name, so the box is never empty for an own entity. */
export function limitOptions(options, limit, extra = {}) {
    if (!limit) return options || [];
    const byId = new Map((options || []).map(o => [String(o.value), o]));
    return limit.map(e => byId.get(e.id) || { value: e.id, label: e.name || e.id, ...extra });
}
